import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import TopMenu from '../../components/TopMenu';
import StatusBadge from '../../components/MockInterview/StatusBadge';
import { useNotification } from '../../notification/NotificationContext';
import {
  getInterviewerRequests,
  rescheduleInterview,
  submitFeedback,
  getFeedbackByRequest,
  extractRoomId,
  getMySlots,
  addMySlot,
  deleteMySlot,
  acceptInterviewRequest,
  declineInterviewRequest,
} from '../../services/mockInterviewService';
import '../../components/MockInterview/MockInterview.css';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }) + ' IST';
  } catch { return iso; }
};

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PREDEFINED_SKILLS = [
  'Communication',
  'Technical Knowledge',
  'Problem Solving',
  'Code Quality',
  'System Design',
  'Attitude',
];

const blankSkillRatings = () => PREDEFINED_SKILLS.map((skill) => ({ skill, score: 5 }));

const scoreColor = (score) => {
  if (score == null) return 'inherit';
  if (score >= 8) return '#22c55e';
  if (score >= 5) return '#f59e0b';
  return '#ef4444';
};

const InterviewerDashboard = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const dark = theme === 'dark';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [newSlot, setNewSlot] = useState('');

  const [feedbackFor, setFeedbackFor] = useState(null);
  const [score, setScore] = useState(7);
  const [feedbackText, setFeedbackText] = useState('');
  const [skillRatings, setSkillRatings] = useState(blankSkillRatings());
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  // Per-request in-flight flag for Accept / Decline so we can disable both buttons
  const [actingId, setActingId] = useState(null);

  // My-slots state ---------------------------------------------------
  const [mySlots, setMySlots] = useState([]);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlotInput, setNewSlotInput] = useState('');
  const [savingSlot, setSavingSlot] = useState(false);

  const load = () =>
    getInterviewerRequests()
      .then((d) => setRequests(Array.isArray(d) ? d : []))
      .catch((e) => showNotification(e.message || 'Failed to load requests', 'error'));

  const loadSlots = () =>
    getMySlots()
      .then((d) => setMySlots(Array.isArray(d) ? d : []))
      .catch((e) => showNotification(e.message || 'Failed to load slots', 'error'));

  useEffect(() => {
    Promise.all([load(), loadSlots()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClick = async (req) => {
    try {
      const fb = await getFeedbackByRequest(req.id);
      setSelectedFeedback(fb ? { ...fb, request: req } : { notFound: true, request: req });
    } catch (e) {
      showNotification(e.message || 'Failed to load feedback', 'error');
    }
  };

  const openReschedule = (req) => {
    setRescheduleFor(req);
    setNewSlot(toLocalInputValue(req.requestedSlot));
  };

  const submitRescheduleForm = async () => {
    if (!newSlot) { showNotification('Pick a new slot', 'warning'); return; }
    setSaving(true);
    try {
      await rescheduleInterview({
        requestId: rescheduleFor.id,
        newSlot: new Date(newSlot).toISOString(),
      });
      showNotification('Reschedule sent', 'success');
      setRescheduleFor(null);
      setNewSlot('');
      await load();
    } catch (e) {
      showNotification(e.message || 'Failed to reschedule', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Open the feedback form. Pre-fill with any existing feedback so reopening
  // shows the previously-submitted values (the POST endpoint is idempotent).
  const openFeedback = async (req) => {
    setFeedbackFor(req);
    setScore(7);
    setFeedbackText('');
    setSkillRatings(blankSkillRatings());
    setFeedbackLoading(true);
    try {
      const existing = await getFeedbackByRequest(req.id);
      if (existing) {
        if (typeof existing.score === 'number') setScore(existing.score);
        if (typeof existing.feedbackText === 'string') setFeedbackText(existing.feedbackText);
        if (Array.isArray(existing.skillRatings) && existing.skillRatings.length) {
          // Merge in saved values, preserving order of PREDEFINED_SKILLS and
          // appending any custom skills the backend returned.
          const map = new Map(existing.skillRatings.map((s) => [s.skill, Number(s.score)]));
          const merged = PREDEFINED_SKILLS.map((skill) => ({
            skill,
            score: map.has(skill) ? map.get(skill) : 5,
          }));
          existing.skillRatings.forEach((s) => {
            if (!PREDEFINED_SKILLS.includes(s.skill)) merged.push({ skill: s.skill, score: Number(s.score) });
          });
          setSkillRatings(merged);
        }
      }
    } catch (e) {
      // 404 is normal — first time leaving feedback. Anything else is informational.
      console.error(e);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const updateSkillScore = (skill, value) => {
    const num = parseFloat(value);
    setSkillRatings((prev) => prev.map((s) => (s.skill === skill ? { ...s, score: num } : s)));
  };

  const submitFeedbackForm = async () => {
    const n = Number(score);
    if (!feedbackText.trim() || !(n >= 1 && n <= 10)) {
      showNotification('Score (1-10) and feedback text are required', 'warning');
      return;
    }
    setSaving(true);
    try {
      await submitFeedback({
        requestId: feedbackFor.id,
        score: n,
        feedbackText: feedbackText.trim(),
        skillRatings,
      });
      showNotification('Feedback submitted', 'success');
      setFeedbackFor(null);
      await load();
    } catch (e) {
      showNotification(e.message || 'Failed to submit feedback', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Accept / Decline ──────────────────────────────────────────────
  const updateRequestInState = (updated) => {
    if (!updated?.id) return;
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };

  const handleAccept = async (req) => {
    setActingId(req.id);
    try {
      const updated = await acceptInterviewRequest(req.id);
      updateRequestInState(updated || { id: req.id, status: 'Confirmed' });
      showNotification('Request accepted', 'success');
    } catch (e) {
      showNotification(e.message || 'Failed to accept', 'error');
    } finally {
      setActingId(null);
    }
  };

  const handleDecline = async (req) => {
    if (!window.confirm('Decline this interview request?')) return;
    setActingId(req.id);
    try {
      const updated = await declineInterviewRequest(req.id);
      updateRequestInState(updated || { id: req.id, status: 'Cancelled' });
      showNotification('Request declined', 'success');
    } catch (e) {
      showNotification(e.message || 'Failed to decline', 'error');
    } finally {
      setActingId(null);
    }
  };

  // ── Slots ─────────────────────────────────────────────────────────
  const submitNewSlot = async () => {
    if (!newSlotInput) { showNotification('Pick a date and time', 'warning'); return; }
    setSavingSlot(true);
    try {
      await addMySlot({ slotDateTime: new Date(newSlotInput).toISOString() });
      showNotification('Slot published', 'success');
      setShowAddSlot(false);
      setNewSlotInput('');
      await loadSlots();
    } catch (e) {
      showNotification(e.message || 'Failed to add slot', 'error');
    } finally {
      setSavingSlot(false);
    }
  };

  const removeSlot = async (slot) => {
    if (slot.isBooked) { showNotification('Booked slots cannot be removed', 'warning'); return; }
    if (!window.confirm('Delete this slot?')) return;
    try {
      await deleteMySlot(slot.id);
      showNotification('Slot removed', 'success');
      await loadSlots();
    } catch (e) {
      showNotification(e.message || 'Failed to delete slot', 'error');
    }
  };

  if (loading) {
    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="mi-loading"><div className="mi-spin" /></div>
      </div>
    );
  }

  return (
    <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="mi-container">
        <button className="mi-btn mi-btn-sm" onClick={() => navigate('/mock-interview')} style={{ marginBottom: 14 }}>
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Back
        </button>
        <p className="mi-kicker">Interviewer</p>
        <h1 className="mi-title">Your Requests</h1>
        <p className="mi-subtitle">Reschedule pending sessions, submit feedback after completion, and review past outcomes.</p>

        <div className="mi-table-wrap">
          <table className="mi-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Requested Slot</th>
                <th>Status</th>
                <th>Meeting</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 && (
                <tr><td colSpan={5}><div className="mi-empty">No requests assigned.</div></td></tr>
              )}
              {requests.map((r) => {
                const isPending = r.status === 'Pending';
                const isConfirmed = r.status === 'Confirmed';
                const isRescheduled = r.status === 'Rescheduled';
                const isCancelled = r.status === 'Cancelled';
                const isCompleted = r.status === 'Completed';
                const acting = actingId === r.id;
                return (
                  <tr key={r.id} onClick={() => handleRowClick(r)}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.candidateName || r.userId}</div>
                      {r.candidateEmail && (
                        <div style={{ fontSize: 12, opacity: 0.6 }}>{r.candidateEmail}</div>
                      )}
                    </td>
                    <td>{fmtDate(r.rescheduledSlot || r.requestedSlot)}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {r.meetingLink && isConfirmed ? (
                        <button
                          className="mi-btn mi-btn-sm"
                          style={{ background: 'rgba(59,130,246,0.15)', borderColor: 'rgba(59,130,246,0.35)', color: '#60a5fa' }}
                          onClick={() => {
                            const roomId = extractRoomId(r.meetingLink);
                            if (roomId) navigate(`/meeting/${roomId}`);
                          }}
                        >
                          Join Meeting
                        </button>
                      ) : '—'}
                    </td>
                    <td>
                      <div className="mi-row" onClick={(e) => e.stopPropagation()} style={{ flexWrap: 'wrap', gap: 6 }}>
                        {isPending && (
                          <>
                            <button
                              className="mi-btn mi-btn-sm mi-btn-success"
                              disabled={acting}
                              onClick={() => handleAccept(r)}
                            >
                              {acting ? '…' : 'Accept'}
                            </button>
                            <button
                              className="mi-btn mi-btn-sm mi-btn-danger"
                              disabled={acting}
                              onClick={() => handleDecline(r)}
                            >
                              {acting ? '…' : 'Decline'}
                            </button>
                          </>
                        )}
                        {isRescheduled && (
                          <span style={{ fontSize: 12, opacity: 0.7 }}>Awaiting candidate confirmation</span>
                        )}
                        {(isPending || isConfirmed) && (
                          <button className="mi-btn mi-btn-sm" onClick={() => openReschedule(r)}>
                            Reschedule
                          </button>
                        )}
                        {(isConfirmed || isCompleted) && (
                          <button className="mi-btn mi-btn-sm mi-btn-primary" onClick={() => openFeedback(r)}>
                            Feedback
                          </button>
                        )}
                        {isCancelled && <span style={{ fontSize: 12, opacity: 0.6 }}>—</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* My Slots ---------------------------------------------------- */}
        <div className="mi-row mi-row-between" style={{ alignItems: 'flex-end', marginTop: 32, marginBottom: 8 }}>
          <h3 className="mi-section-title" style={{ margin: 0 }}>My Slots</h3>
          <button className="mi-btn mi-btn-primary mi-btn-sm" onClick={() => setShowAddSlot(true)}>
            <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Add Slot
          </button>
        </div>
        <div className="mi-table-wrap">
          <table className="mi-table">
            <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mySlots.length === 0 && (
                <tr><td colSpan={3}><div className="mi-empty">No slots published. Click "Add Slot" to publish your availability.</div></td></tr>
              )}
              {mySlots.map((s) => (
                <tr key={s.id}>
                  <td>{fmtDate(s.slotDateTime)}</td>
                  <td>
                    <StatusBadge status={s.isBooked ? 'Booked' : 'Open'} />
                  </td>
                  <td>
                    {!s.isBooked && (
                      <button
                        className="mi-btn mi-btn-sm mi-btn-danger"
                        onClick={() => removeSlot(s)}
                        title="Delete slot"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {rescheduleFor && (
        <div className="mi-modal-backdrop" onClick={() => setRescheduleFor(null)}>
          <div className="mi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mi-modal-head">
              <h3>Reschedule Interview</h3>
              <button className="mi-modal-close" onClick={() => setRescheduleFor(null)}>×</button>
            </div>
            <div className="mi-field">
              <label>New Slot</label>
              <input
                type="datetime-local"
                className="mi-input"
                value={newSlot}
                onChange={(e) => setNewSlot(e.target.value)}
              />
            </div>
            <div className="mi-row mi-row-end">
              <button className="mi-btn" onClick={() => setRescheduleFor(null)}>Cancel</button>
              <button className="mi-btn mi-btn-primary" onClick={submitRescheduleForm} disabled={saving}>
                {saving ? 'Saving…' : 'Send reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {feedbackFor && (
        <div className="mi-modal-backdrop" onClick={() => setFeedbackFor(null)}>
          <div className="mi-modal mi-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="mi-modal-head">
              <h3>Submit Feedback</h3>
              <button className="mi-modal-close" onClick={() => setFeedbackFor(null)}>×</button>
            </div>

            {feedbackLoading ? (
              <div style={{ padding: 24, textAlign: 'center', opacity: 0.6 }}>Loading existing feedback…</div>
            ) : (
              <>
                <div className="mi-field">
                  <label>Overall Score (1-10): <strong style={{ color: scoreColor(score) }}>{score}</strong></label>
                  <input
                    type="range"
                    min={1}
                    max={10}
                    step={0.5}
                    value={score}
                    onChange={(e) => setScore(parseFloat(e.target.value))}
                  />
                </div>

                <div className="mi-field">
                  <label>Feedback</label>
                  <textarea
                    className="mi-textarea"
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Overall performance, strengths, improvements…"
                  />
                </div>

                <div className="mi-field">
                  <label>Skill Ratings</label>
                  <div className="mi-skill-list">
                    {skillRatings.map(({ skill, score: sScore }) => (
                      <div key={skill} className="mi-skill-row">
                        <span className="mi-skill-label">{skill}</span>
                        <input
                          type="range"
                          min={1}
                          max={10}
                          step={0.5}
                          value={sScore}
                          onChange={(e) => updateSkillScore(skill, e.target.value)}
                          className="mi-skill-range"
                        />
                        <span className="mi-skill-score" style={{ color: scoreColor(sScore) }}>{sScore}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mi-row mi-row-end">
                  <button className="mi-btn" onClick={() => setFeedbackFor(null)}>Cancel</button>
                  <button className="mi-btn mi-btn-primary" onClick={submitFeedbackForm} disabled={saving}>
                    {saving ? 'Saving…' : 'Submit feedback'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {selectedFeedback && (
        <div className="mi-modal-backdrop" onClick={() => setSelectedFeedback(null)}>
          <div className="mi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mi-modal-head">
              <h3>Existing Feedback</h3>
              <button className="mi-modal-close" onClick={() => setSelectedFeedback(null)}>×</button>
            </div>
            {selectedFeedback.notFound ? (
              <p style={{ opacity: 0.7 }}>No feedback submitted yet for this request.</p>
            ) : (
              <>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Score:</strong>{' '}
                  <span style={{ color: scoreColor(selectedFeedback.score) }}>
                    {selectedFeedback.score}/10
                  </span>
                </p>
                <p style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap', opacity: 0.9 }}>
                  {selectedFeedback.feedbackText}
                </p>
                {Array.isArray(selectedFeedback.skillRatings) && selectedFeedback.skillRatings.length > 0 && (
                  <div className="mi-skill-list" style={{ marginTop: 12 }}>
                    {selectedFeedback.skillRatings.map((sr) => (
                      <div key={sr.skill} className="mi-skill-row">
                        <span className="mi-skill-label">{sr.skill}</span>
                        <div className="mi-skill-bar">
                          <div
                            className="mi-skill-bar-fill"
                            style={{
                              width: `${(Number(sr.score) / 10) * 100}%`,
                              background: scoreColor(Number(sr.score)),
                            }}
                          />
                        </div>
                        <span className="mi-skill-score" style={{ color: scoreColor(Number(sr.score)) }}>
                          {sr.score}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <p style={{ fontSize: 12, opacity: 0.6, marginTop: 10 }}>
                  Submitted {fmtDate(selectedFeedback.createdAt)}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {showAddSlot && (
        <div className="mi-modal-backdrop" onClick={() => setShowAddSlot(false)}>
          <div className="mi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mi-modal-head">
              <h3>Add Available Slot</h3>
              <button className="mi-modal-close" onClick={() => setShowAddSlot(false)}>×</button>
            </div>
            <div className="mi-field">
              <label>Date &amp; Time</label>
              <input
                type="datetime-local"
                className="mi-input"
                value={newSlotInput}
                onChange={(e) => setNewSlotInput(e.target.value)}
              />
            </div>
            <div className="mi-row mi-row-end">
              <button className="mi-btn" onClick={() => setShowAddSlot(false)}>Cancel</button>
              <button className="mi-btn mi-btn-primary" onClick={submitNewSlot} disabled={savingSlot}>
                {savingSlot ? 'Saving…' : 'Publish slot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewerDashboard;
